import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, ArrowUp, LogOut, Trash2, Upload } from "lucide-react";
import { api, imgSrc, SUBS } from "../lib/api";
import { Logo } from "../components/Logo";

const CATS = [
    ["futebol", "FUTEBOL"],
    ["retro", "RETRÔ"],
    ["basquete", "BASQUETE"],
];

const FILTERS = [["todas", "TODAS"], ...CATS];

export default function Admin() {
    const [auth, setAuth] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [products, setProducts] = useState([]);
    const [whatsapp, setWhatsapp] = useState("");
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [category, setCategory] = useState("futebol");
    const [sub, setSub] = useState("brasileirao");
    const [filter, setFilter] = useState("todas");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        const [p, s] = await Promise.all([api.get("/products"), api.get("/settings")]);
        setProducts(p.data);
        setWhatsapp(s.data.whatsapp_number || "");
    }, []);

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => {
                setAuth(r.data);
                load();
            })
            .catch(() => setAuth(false));
    }, [load]);

    const login = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("cc_token", data.access_token);
            setAuth(data.user);
            load();
            toast.success("Bem-vindo ao painel");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Falha no login");
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        localStorage.removeItem("cc_token");
        setAuth(false);
    };

    const saveWhatsapp = async () => {
        const { data } = await api.put("/settings", { whatsapp_number: whatsapp });
        setWhatsapp(data.whatsapp_number);
        toast.success("Número do WhatsApp salvo");
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (newPw !== confirmPw) {
            toast.error("A confirmação não confere com a nova senha");
            return;
        }
        try {
            await api.post("/auth/change-password", {
                current_password: currentPw,
                new_password: newPw,
            });
            setCurrentPw("");
            setNewPw("");
            setConfirmPw("");
            toast.success("Senha alterada com sucesso");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Erro ao alterar a senha");
        }
    };

    const upload = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Escolha uma imagem primeiro");
            return;
        }
        const formData = new FormData();
        formData.append("category", category);
        if (category === "futebol") formData.append("subcategory", sub);
        formData.append("file", file);
        setUploading(true);
        try {
            await api.post("/products", formData);
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            await load();
            toast.success("Foto adicionada ao catálogo");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Erro no envio");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        await api.delete(`/products/${id}`);
        await load();
        toast.success("Foto removida");
    };

    const changeCategory = async (id, cat) => {
        await api.patch(`/products/${id}`, { category: cat });
        await load();
        toast.success("Categoria atualizada");
    };

    const changeSubcategory = async (id, subId) => {
        await api.patch(`/products/${id}`, { subcategory: subId });
        await load();
        toast.success("Campeonato atualizado");
    };

    const visible = filter === "todas" ? products : products.filter((p) => p.category === filter);

    const move = async (id, dir) => {
        const i = visible.findIndex((p) => p.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= visible.length) return;
        const ids = products.map((p) => p.id);
        const a = ids.indexOf(visible[i].id);
        const b = ids.indexOf(visible[j].id);
        [ids[a], ids[b]] = [ids[b], ids[a]];
        const byId = Object.fromEntries(products.map((p) => [p.id, p]));
        setProducts(ids.map((pid) => byId[pid]));
        await api.put("/products/reorder", { ids });
    };

    if (auth === null) {
        return (
            <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
                <span className="mono-label">Carregando…</span>
            </div>
        );
    }

    if (auth === false) {
        return (
            <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center px-4">
                <form
                    data-testid="admin-login-form"
                    onSubmit={login}
                    className="w-full max-w-sm border border-[#27272A] bg-[#141414] p-8"
                >
                    <div className="flex items-center gap-3">
                        <Logo size={32} />
                        <div>
                            <p className="mono-label">Painel Admin</p>
                            <h1 className="font-display font-extrabold uppercase tracking-tight text-lg">
                                Campo & Concreto
                            </h1>
                        </div>
                    </div>
                    <label className="mt-8 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                        E-mail
                    </label>
                    <input
                        data-testid="admin-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 w-full border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                    />
                    <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                        Senha
                    </label>
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 w-full border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                    />
                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        className="mt-6 w-full bg-[#FF6A00] py-3 font-condensed font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#FF8533]"
                    >
                        Entrar
                    </button>
                    <Link
                        to="/"
                        className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] hover:text-[#A1A1AA]"
                    >
                        <ArrowLeft size={12} /> Voltar ao catálogo
                    </Link>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="admin-panel" className="min-h-screen bg-[#0B0B0B] text-white">
            <header className="border-b border-[#27272A] bg-[#0B0B0B]/90 backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={26} />
                        <span className="font-display font-extrabold uppercase tracking-tight text-sm">
                            Painel Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="hidden sm:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white"
                        >
                            <ArrowLeft size={12} /> Ver catálogo
                        </Link>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="flex items-center gap-2 border border-[#27272A] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#FF6A00] hover:text-[#FF6A00]"
                        >
                            <LogOut size={12} /> Sair
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-10">
                {!whatsapp && (
                    <div
                        data-testid="whatsapp-warning"
                        className="border border-[#FF6A00]/50 bg-[#FF6A00]/10 px-5 py-4"
                    >
                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF6A00]">
                            Configure o número do WhatsApp abaixo para ativar os botões de pedido
                            no catálogo.
                        </p>
                    </div>
                )}

                <section className="border border-[#27272A] bg-[#141414] p-6">
                    <p className="mono-label">// WhatsApp da loja</p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <input
                            data-testid="whatsapp-number-input"
                            type="tel"
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            placeholder="Ex: 5511998887777 (DDD + número)"
                            className="flex-1 border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                        />
                        <button
                            data-testid="whatsapp-save-button"
                            onClick={saveWhatsapp}
                            className="bg-[#FF6A00] px-6 py-3 font-condensed font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#FF8533]"
                        >
                            Salvar número
                        </button>
                    </div>
                    <p className="mt-3 text-xs text-[#A1A1AA]">
                        Use o formato internacional apenas com números: 55 + DDD + número.
                    </p>
                </section>

                <section className="border border-[#27272A] bg-[#141414] p-6">
                    <p className="mono-label">// Trocar senha de acesso</p>
                    <form
                        data-testid="change-password-form"
                        onSubmit={changePassword}
                        className="mt-4 flex flex-col gap-3"
                    >
                        <input
                            data-testid="current-password-input"
                            type="password"
                            required
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            placeholder="Senha atual"
                            className="border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                        />
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                data-testid="new-password-input"
                                type="password"
                                required
                                minLength={6}
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="Nova senha (mín. 6 caracteres)"
                                className="flex-1 border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                            />
                            <input
                                data-testid="confirm-password-input"
                                type="password"
                                required
                                value={confirmPw}
                                onChange={(e) => setConfirmPw(e.target.value)}
                                placeholder="Confirmar nova senha"
                                className="flex-1 border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm outline-none focus:border-[#FF6A00]"
                            />
                        </div>
                        <button
                            data-testid="change-password-submit"
                            type="submit"
                            className="bg-[#FF6A00] px-6 py-3 font-condensed font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#FF8533]"
                        >
                            Alterar senha
                        </button>
                    </form>
                </section>

                <section className="border border-[#27272A] bg-[#141414] p-6">
                    <p className="mono-label">// Adicionar foto ao catálogo</p>
                    <form onSubmit={upload} className="mt-4 flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                ref={fileRef}
                                data-testid="upload-file-input"
                                type="file"
                                accept="image/*"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="flex-1 border border-dashed border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm text-[#A1A1AA] file:mr-4 file:border-0 file:bg-[#27272A] file:px-4 file:py-2 file:font-condensed file:font-bold file:uppercase file:tracking-widest file:text-white"
                            />
                            <select
                                data-testid="upload-category-select"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm uppercase outline-none focus:border-[#FF6A00]"
                            >
                                {CATS.map(([id, label]) => (
                                    <option key={id} value={id}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                            {category === "futebol" && (
                                <select
                                    data-testid="upload-subcategory-select"
                                    value={sub}
                                    onChange={(e) => setSub(e.target.value)}
                                    className="border border-[#27272A] bg-[#0B0B0B] px-4 py-3 text-sm uppercase outline-none focus:border-[#FF6A00]"
                                >
                                    {SUBS.map(([id, label]) => (
                                        <option key={id} value={id}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <button
                            data-testid="upload-submit-button"
                            type="submit"
                            disabled={uploading}
                            className="flex items-center justify-center gap-2 bg-[#FF6A00] px-6 py-3 font-condensed font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#FF8533] disabled:opacity-50"
                        >
                            <Upload size={16} /> {uploading ? "Enviando…" : "Adicionar ao catálogo"}
                        </button>
                    </form>
                </section>

                <section>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <p className="mono-label">// Peças cadastradas ({products.length})</p>
                        <div className="flex gap-2">
                            {FILTERS.map(([id, label]) => (
                                <button
                                    key={id}
                                    data-testid={`admin-filter-${id}`}
                                    onClick={() => setFilter(id)}
                                    className={`border px-3 py-1.5 font-condensed font-bold uppercase tracking-widest text-xs transition-colors ${
                                        filter === id
                                            ? "border-[#FF6A00] bg-[#FF6A00] text-black"
                                            : "border-[#27272A] text-[#A1A1AA] hover:text-white"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {visible.length === 0 ? (
                        <div
                            data-testid="admin-empty-state"
                            className="mt-6 border border-dashed border-[#27272A] py-16 text-center"
                        >
                            <p className="mono-label">Nenhuma foto cadastrada</p>
                        </div>
                    ) : (
                        <div
                            data-testid="admin-product-list"
                            className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
                        >
                            {visible.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`admin-product-${p.id}`}
                                    className="border border-[#27272A] bg-[#141414]"
                                >
                                    <div className="aspect-[3/4] overflow-hidden bg-[#0B0B0B]">
                                        <img
                                            src={imgSrc(p.image_url)}
                                            alt=""
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="p-3 space-y-2">
                                        <p className="font-mono text-[10px] uppercase tracking-wider text-[#A1A1AA] truncate">
                                            {p.ref}
                                        </p>
                                        <select
                                            data-testid={`admin-category-${p.id}`}
                                            value={p.category}
                                            onChange={(e) => changeCategory(p.id, e.target.value)}
                                            className="w-full border border-[#27272A] bg-[#0B0B0B] px-2 py-1.5 text-xs uppercase outline-none focus:border-[#FF6A00]"
                                        >
                                            {CATS.map(([id, label]) => (
                                                <option key={id} value={id}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                        {p.category === "futebol" && (
                                            <select
                                                data-testid={`admin-subcategory-${p.id}`}
                                                value={p.subcategory || "brasileirao"}
                                                onChange={(e) =>
                                                    changeSubcategory(p.id, e.target.value)
                                                }
                                                className="w-full border border-[#27272A] bg-[#0B0B0B] px-2 py-1.5 text-xs uppercase outline-none focus:border-[#FF6A00]"
                                            >
                                                {SUBS.map(([id, label]) => (
                                                    <option key={id} value={id}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        <div className="flex gap-2">
                                            <button
                                                data-testid={`move-up-${p.id}`}
                                                onClick={() => move(p.id, -1)}
                                                aria-label="Mover para cima"
                                                className="flex-1 flex items-center justify-center border border-[#27272A] py-2 text-[#A1A1AA] hover:border-[#FF6A00] hover:text-[#FF6A00]"
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                data-testid={`move-down-${p.id}`}
                                                onClick={() => move(p.id, 1)}
                                                aria-label="Mover para baixo"
                                                className="flex-1 flex items-center justify-center border border-[#27272A] py-2 text-[#A1A1AA] hover:border-[#FF6A00] hover:text-[#FF6A00]"
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                            <button
                                                data-testid={`delete-product-${p.id}`}
                                                onClick={() => remove(p.id)}
                                                aria-label="Remover"
                                                className="flex-1 flex items-center justify-center border border-[#27272A] py-2 text-[#A1A1AA] hover:border-red-500 hover:text-red-500"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}
